import { AsyncPipe } from '@angular/common'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatMenuModule } from '@angular/material/menu'
import { MatTooltip } from '@angular/material/tooltip'
import { ActivatedRoute, Router } from '@angular/router'
import { injectQueryClient } from '@ngneat/query'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { EditorModule } from '@tinymce/tinymce-angular'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, lastValueFrom, map, Subscription, switchMap, take, tap, timer } from 'rxjs'
import { Call, CallsService, MetricType } from '../../calls.service'
import { ConfigurationService } from '../../configuration.service'
import { LoggerService } from '../../logger.service'
import { MetricsService } from '../../metrics.service'
import { queryKeys } from '../../query-keys'
import { SpinnerComponent } from '../../spinner/spinner.component'
import { ToastService } from '../../toast.service'
import { UserNamePipe } from '../../user-name.pipe'
import { User } from '../../users.service'
import { formatDateWithMilliseconds } from '../../utils'
import * as EditorCitations from './editor-citations'
import { getEditorStyles } from './editor-styles'

const llmLiveDefaultTimeout = 60

export type LlmAction =
  | 'elaborate'
  | 'shorten'
  | 'bulletize'
  | 'prosify'
  | 'make_active_voice'
  | 'make_passive_voice'
  | 'make_past_tense'
  | 'anonymize'
  | 'name_names'
  | 'fact_check'

type LockEditorResponse = {
  lockObtained: boolean
}

type LockEditorFailureResponse = LockEditorResponse & {
  user: User
  lockedUntil: Date
}

function isEditorLockFailureResponse(response: LockEditorResponse): response is LockEditorFailureResponse {
  return !response.lockObtained
}

type FactCheckResolution = {
  statement: string
  answer: 'True' | 'False' | 'Unclear'
  reason: string
  newWording: string | null
}

type SentenceIdPair = {
  startSentenceId: string
  endSentenceId: string
}

type LiveSyncResponse = FactCheckResolution[]

type RevisionAuthor = {
  id: string
  name: string
  avatar: string | null
}

type Revision = {
  revisionId: string
  createdAt: Date
  content: string | null
  author: RevisionAuthor | null
}

function generateAvatar(name: string | null) {
  const colors = ['#2DC26B', '#F1C40F', '#E03E2D', '#B96AD9', '#3598DB', '#169179', '#E67E23', '#BA372A', '#843FA1', '#236FA1', '#95A5A6', '#7E8C8D', '#34495E']
  const initialLetter =
    name !== null
      ? name
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase())
          .join('')
          .substring(0, 2)
      : 'A'
  const color = initialLetter.charCodeAt(0) % colors.length
  return `data:image/svg+xml, ${encodeURIComponent(`<svg height="36" width="36" xmlns="http://www.w3.org/2000/svg"><circle cx="18" cy="18" r="18" fill="${color}"/><text x="50%" y="50%" text-anchor="middle" dominant-baseline="central" fill="#FFF" font-family="sans-serif" font-size="18">${initialLetter}</text></svg>`)}`
}

@Component({
  selector: 'app-editor',
  standalone: true,
  imports: [
    AsyncPipe,
    EditorModule,
    FormsModule,
    SpinnerComponent,
    TranslateModule,
    MatTooltip,
    MatMenuModule,
    MatButton,
    NgxSkeletonLoaderModule,
    UserNamePipe,
  ],
  templateUrl: './editor.component.html',
  styleUrl: './editor.component.scss',
})
export class EditorComponent implements OnInit, OnDestroy {
  @Input()
  call!: Call

  @Input()
  canUserEdit!: boolean

  @Input()
  canUserComment!: boolean

  @Output()
  isLocked = new EventEmitter<boolean>()

  @Output()
  openAgendaUpload = new EventEmitter<void>()

  @Output()
  openMeeting = new EventEmitter<void>()

  @Output()
  openDocument = new EventEmitter<{ resourceId: number; resourcePageNumber: number }>()

  configurationService = inject(ConfigurationService)
  metricsService = inject(MetricsService)
  callsService = inject(CallsService)
  http = inject(HttpClient)
  router = inject(Router)
  route = inject(ActivatedRoute)
  toastService = inject(ToastService)
  translateService = inject(TranslateService)
  logger = inject(LoggerService)
  queryClient = injectQueryClient()
  configuration$ = this.configurationService.getConfiguration()
  approveMissingAgendaMutation = this.callsService.approveMissingAgendaMutation()

  editor!: any

  isLoading = false
  loadingTextTimeout: any | null = null

  save$ = new BehaviorSubject<string>('')

  init!: any
  editorLockFailureResponse: LockEditorFailureResponse | null = null
  private saveSubscription!: Subscription
  private editorLockSubscription: Subscription | null = null
  private queryParamsSubscription: Subscription | null = null
  isEditorInitialized = false

  ngOnInit(): void {
    this.init = this.generateEditorInit()
    this.saveSubscription = this.save$.pipe(debounceTime(2000)).subscribe((value) => this.saveContent(value))

    this.whenEditorIsInitialized().subscribe(() => {
      this.isEditorInitialized = true
    })

    this.queryParamsSubscription = this.route.queryParams
      .pipe(
        map((params) => params['templateItem'] ?? null),
        filter((templateItemId) => templateItemId !== null),
        distinctUntilChanged(),
        switchMap((templateItemId) => this.whenEditorIsInitialized().pipe(map(() => templateItemId)))
      )
      .subscribe((templateItemId) => {
        this.scrollToTemplateItem(Number(templateItemId))
        this.router.navigate([], {
          queryParams: { templateItem: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        })
      })
  }

  get hasLock(): boolean {
    return this.editorLockFailureResponse === null
  }
  get isDisabled(): boolean {
    return (!this.canUserEdit && !this.canUserComment) || this.call.isSpeechMissing || this.call.processingStatus !== null
  }

  private setupEditor(editor: any, translateService: TranslateService) {
    this.obtainAndMaintainLockForEditor()
    // TODO This shouldn't be needed really..
    const _this = this
    this.editor = editor

    let storedStartSentenceId: string | null = null
    let storedEndSentenceId: string | null = null
    editor.on('copy cut', (e: any) => {
      storedStartSentenceId = e.target?.dataset?.startSentenceId ?? null
      storedEndSentenceId = e.target?.dataset?.endSentenceId ?? null
    })
    editor.on('paste', () => {
      setTimeout(() => {
        const currentNode = editor.selection.getNode()
        const { startSentenceId } = this.getSentenceIds(currentNode)
        const pastedNodeLinked = startSentenceId !== null
        if (!pastedNodeLinked && storedStartSentenceId !== null) {
          currentNode.dataset.startSentenceId = storedStartSentenceId
          currentNode.dataset.endSentenceId = storedEndSentenceId
        }
      }, 0)
    })

    editor.on('VersionRestored', async () => {
      await this.saveContent(
        EditorCitations.getContentWithStrippedCitationsFromEditor({
          editor,
          citations: this.call.minutesCitations,
          eventEmitters: {
            openDocument: this.openDocument,
            openMeeting: this.openMeeting,
          },
        }),
        true
      )
      await this.metricsService.sendMetric(this.call.id, MetricType.RESTORE_REVISION)
    })

    if (this.canUserEdit) {
      editor.ui.registry.addButton('reset', {
        text: 'Reset',
        tooltip: 'Reset',
        onAction: async () => {
          this.reset()
        },
      })
    }
    editor.ui.registry.addIcon(
      'ai_assistant',
      '<svg height="25" style="enable-background:new 0 0 48 48;" version="1.1" viewBox="0 0 48 48" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><style type="text/css">.st0{fill:none;stroke:#505050;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:10;}.st1{fill:none;stroke:#505050;stroke-width:2;stroke-linejoin:round;stroke-miterlimit:10;}.st2{fill:none;stroke:#505050;stroke-width:2;stroke-miterlimit:10;}.st3{fill:none;stroke:#505050;stroke-width:2;stroke-linecap:round;stroke-miterlimit:10;}</style><g id="_x34_8x48"/><g id="icons"><g><path class="st0" d="M16,2c-1,7.5-3.8,13.9-15,15c11.2,1.1,14,7.5,15,15c1-7.5,2.8-13.9,14-15C18.8,15.9,17,9.5,16,2z"/><path class="st0" d="M38,36c0.6-4.5,2.3-8.3,9-9c-6.7-0.7-8.4-4.5-9-9c-0.6,4.5-2.3,8.3-9,9C35.7,27.7,37.4,31.5,38,36z"/><path class="st0" d="M23,47c0.5-3.5,1.8-6.5,7-7c-5.2-0.5-6.5-3.5-7-7c-0.5,3.5-1.8,6.5-7,7C21.2,40.5,22.5,43.5,23,47z"/></g></g></svg>'
    )
    editor.ui.registry.addMenuButton('ai_assistant', {
      text: translateService.instant('EDITOR.AI_ACTIONS'),
      icon: 'ai_assistant',
      fetch(callback: any, fetchContext: any) {
        const items = [
          {
            type: 'menuitem',
            text: translateService.instant('EDITOR.ACTIONS.ELABORATE'),
            icon: 'elaborate',
            onAction: () => {
              _this.performLlmLiveRequest(editor, 'elaborate', translateService.instant('EDITOR.ACTIONS.ELABORATE'), llmLiveDefaultTimeout, true)
            },
          },
          {
            type: 'menuitem',
            text: translateService.instant('EDITOR.ACTIONS.SHORTEN'),
            icon: 'shorten',
            onAction: () => {
              _this.performLlmLiveRequest(editor, 'shorten', translateService.instant('EDITOR.ACTIONS.SHORTEN'), llmLiveDefaultTimeout, true)
            },
          },
          {
            type: 'menuitem',
            text: translateService.instant('EDITOR.ACTIONS.MAKE_ACTIVE_VOICE'),
            icon: 'make_active_voice',
            onAction: () => {
              _this.performLlmLiveRequest(
                editor,
                'make_active_voice',
                translateService.instant('EDITOR.ACTIONS.MAKE_ACTIVE_VOICE'),
                llmLiveDefaultTimeout,
                true
              )
            },
          },
          {
            type: 'menuitem',
            text: translateService.instant('EDITOR.ACTIONS.MAKE_PASSIVE_VOICE'),
            icon: 'make_passive_voice',
            onAction: () => {
              _this.performLlmLiveRequest(
                editor,
                'make_passive_voice',
                translateService.instant('EDITOR.ACTIONS.MAKE_PASSIVE_VOICE'),
                llmLiveDefaultTimeout,
                true
              )
            },
          },
          {
            type: 'menuitem',
            text: translateService.instant('EDITOR.ACTIONS.MAKE_PAST_TENSE'),
            icon: 'make_past_tense',
            onAction: () => {
              _this.performLlmLiveRequest(editor, 'make_past_tense', translateService.instant('EDITOR.ACTIONS.MAKE_PAST_TENSE'), llmLiveDefaultTimeout, true)
            },
          },
          {
            type: 'menuitem',
            text: translateService.instant('EDITOR.ACTIONS.ANONYMIZE'),
            icon: 'anonymize',
            onAction: () => {
              _this.performLlmLiveRequest(editor, 'anonymize', translateService.instant('EDITOR.ACTIONS.ANONYMIZE'), llmLiveDefaultTimeout, true)
            },
          },
          {
            type: 'menuitem',
            text: translateService.instant('EDITOR.ACTIONS.NAME_NAMES'),
            icon: 'name_names',
            tooltip: 'Name names',
            onAction: () => {
              _this.performLlmLiveRequest(editor, 'name_names', translateService.instant('EDITOR.ACTIONS.NAME_NAMES'), llmLiveDefaultTimeout, true)
            },
          },
          {
            type: 'menuitem',
            text: translateService.instant('EDITOR.ACTIONS.FACT_CHECK_STATEMENT'),
            icon: 'fact_check',
            onAction: () => {
              _this.performLlmLiveRequest(editor, 'fact_check', translateService.instant('EDITOR.ACTIONS.FACT_CHECK_STATEMENT'), llmLiveDefaultTimeout, false)
            },
          },
        ]
        const input = fetchContext.pattern
        if (input.length > 0) {
          callback(items.filter((item) => item.text.toLowerCase().includes(input.toLowerCase())))
        } else {
          callback(items)
        }
      },
      search: { placeholder: 'Search Action' },
    })
    editor.on('change', () => {
      // Using a macro task ensures the editor content has already been updated when change is triggered on editor reset
      setTimeout(() =>
        this.save$.next(
          EditorCitations.getContentWithStrippedCitationsFromEditor({
            editor,
            citations: this.call.minutesCitations,
            eventEmitters: {
              openDocument: this.openDocument,
              openMeeting: this.openMeeting,
            },
          })
        )
      )
    })
    editor.on('KeyUp', () => {
      setTimeout(() =>
        this.save$.next(
          EditorCitations.getContentWithStrippedCitationsFromEditor({
            editor,
            citations: this.call.minutesCitations,
            eventEmitters: {
              openDocument: this.openDocument,
              openMeeting: this.openMeeting,
            },
          })
        )
      )
    })

    editor.on('click', (e: any) => {
      if (e.target === null) {
        return
      }
      const { startSentenceId, endSentenceId } = this.getSentenceIds(e.target)
      if (startSentenceId !== null) {
        this.scrollTranscriptToSentenceIds(startSentenceId, endSentenceId)
      }
    })

    editor.ui.registry.addIcon(
      'elaborate',
      '<svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M2 8C2 7.44772 2.44772 7 3 7H21C21.5523 7 22 7.44772 22 8C22 8.55228 21.5523 9 21 9H3C2.44772 9 2 8.55228 2 8Z" fill="currentColor"/><path d="M2 12C2 11.4477 2.44772 11 3 11H21C21.5523 11 22 11.4477 22 12C22 12.5523 21.5523 13 21 13H3C2.44772 13 2 12.5523 2 12Z" fill="currentColor"/><path d="M3 15C2.44772 15 2 15.4477 2 16C2 16.5523 2.44772 17 3 17H15C15.5523 17 16 16.5523 16 16C16 15.4477 15.5523 15 15 15H3Z" fill="currentColor"/></svg>'
    )
    editor.ui.registry.addIcon(
      'shorten',
      '<svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M3 9C2.44772 9 2 9.44772 2 10C2 10.5523 2.44772 11 3 11H21C21.5523 11 22 10.5523 22 10C22 9.44772 21.5523 9 21 9H3Z" fill="currentColor"/><path d="M3 13C2.44772 13 2 13.4477 2 14C2 14.5523 2.44772 15 3 15H15C15.5523 15 16 14.5523 16 14C16 13.4477 15.5523 13 15 13H3Z" fill="currentColor"/></svg>'
    )
    editor.ui.registry.addIcon(
      'bulletize',
      '<svg enable-background="new 0 0 32 32" height="32px" id="Слой_1" version="1.1" viewBox="0 0 32 32" width="32px" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g id="List"><path d="M11.98,6.99l19,0.02c0.552,0,1-0.448,1-1s-0.448-1-1-1l-19-0.02c-0.552,0-1,0.448-1,1   C10.98,6.542,11.428,6.99,11.98,6.99z" fill="#121313"/><path d="M31,15l-19-0.02c-0.552,0-1,0.448-1,1s0.448,1,1,1L31,17c0.552,0,1-0.448,1-1C32,15.448,31.552,15,31,15z" fill="#121313"/><path d="M31,25l-19-0.02c-0.552,0-1,0.448-1,1c0,0.552,0.448,1,1,1L31,27c0.552,0,1-0.448,1-1   C32,25.448,31.552,25,31,25z" fill="#121313"/><path d="M3.99,2.02C1.786,2.02,0,3.806,0,6.01C0,8.214,1.786,10,3.99,10c2.204,0,3.99-1.786,3.99-3.99   C7.98,3.806,6.194,2.02,3.99,2.02z M3.98,8.01c-1.124,0-2.025-0.876-2.025-2s0.901-2,2.025-2c1.124,0,2,0.876,2,2   S5.104,8.01,3.98,8.01z" fill="#121313"/><path d="M3.99,12.02C1.786,12.02,0,13.806,0,16.01C0,18.214,1.786,20,3.99,20c2.204,0,3.99-1.786,3.99-3.99   C7.98,13.806,6.194,12.02,3.99,12.02z M3.98,18.01c-1.124,0-2.025-0.876-2.025-2s0.901-2,2.025-2c1.124,0,2,0.876,2,2   S5.104,18.01,3.98,18.01z" fill="#121313"/><path d="M3.99,22C1.786,22,0,23.786,0,25.99c0,2.204,1.786,3.99,3.99,3.99c2.204,0,3.99-1.786,3.99-3.99   C7.98,23.786,6.194,22,3.99,22z M3.98,27.99c-1.124,0-2.025-0.876-2.025-2s0.901-2,2.025-2c1.124,0,2,0.876,2,2   S5.104,27.99,3.98,27.99z" fill="#121313"/></g><g/><g/><g/><g/><g/><g/></svg>'
    )
    editor.ui.registry.addIcon(
      'prosify',
      '<svg enable-background="new 0 0 32 32" height="32px" id="Слой_1" version="1.1" viewBox="0 0 32 32" width="32px" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g id="Menu"><path d="M1,7h30c0.552,0,1-0.448,1-1c0-0.552-0.448-1-1-1H1C0.448,5,0,5.448,0,6C0,6.552,0.448,7,1,7z" fill="#121313"/><path d="M31,15H1c-0.552,0-1,0.448-1,1c0,0.552,0.448,1,1,1h30c0.552,0,1-0.448,1-1C32,15.448,31.552,15,31,15z" fill="#121313"/><path d="M31,25H1c-0.552,0-1,0.448-1,1c0,0.552,0.448,1,1,1h30c0.552,0,1-0.448,1-1C32,25.448,31.552,25,31,25z" fill="#121313"/></g><g/><g/><g/><g/><g/><g/></svg>'
    )
    editor.ui.registry.addIcon(
      'make_active_voice',
      '<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"><g data-name="Turn Right" id="Turn_Right-2"><path d="M61.563,23.174l-22-15A1,1,0,0,0,38,9v7H22a20,20,0,0,0,0,40h9a1,1,0,0,0,1-1V41a1,1,0,0,0-1-1H22a4,4,0,0,1,0-8H38v7a1,1,0,0,0,1.563.826l22-15a1,1,0,0,0,0-1.652ZM40,37.107V31a1,1,0,0,0-1-1H22a6,6,0,0,0,0,12h8V54H22a18,18,0,0,1,0-36H39a1,1,0,0,0,1-1V10.893L59.225,24Z"/></g></svg>'
    )
    editor.ui.registry.addIcon(
      'make_passive_voice',
      '<svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"><g data-name="Turn Left" id="Turn_Left-2"><path d="M42,16H26V9a1,1,0,0,0-1.563-.826l-22,15a1,1,0,0,0,0,1.652l22,15A1,1,0,0,0,26,39V32H42a4,4,0,0,1,0,8H33a1,1,0,0,0-1,1V55a1,1,0,0,0,1,1h9a20,20,0,0,0,0-40Zm0,38H34V42h8a6,6,0,0,0,0-12H25a1,1,0,0,0-1,1v6.107L4.775,24,24,10.893V17a1,1,0,0,0,1,1H42a18,18,0,0,1,0,36Z"/></g></svg>'
    )
    editor.ui.registry.addIcon(
      'make_past_tense',
      '<svg height="100%" style="shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd" viewBox="0 0 1.26433 1.27" width="100%" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><defs><style type="text/css"><![CDATA[.fil0 {fill:none}.fil2 {fill:#191919}.fil1 {fill:#191919;fill-rule:nonzero}]]></style></defs><g id="图层_x0020_1"><rect class="fil0" height="0.92991" transform="matrix(1.08027E-013 -1.35963 -1.35963 -1.08027E-013 1.26433 1.27)" width="0.93408"/><g id="_208089664"><path class="fil1" d="M0.19932 0.62365c-0.00054,0.02328 -0.01988,0.04172 -0.04316,0.04118 -0.02328,-0.00054 -0.04172,-0.01988 -0.04118,-0.04316 0.00359,-0.15059 0.06699,-0.28604 0.16713,-0.3838 0.10018,-0.0978 0.23709,-0.15795 0.38765,-0.15795 0.15323,0 0.29202,0.06215 0.39247,0.1626 0.10045,0.10046 0.1626,0.23923 0.1626,0.39248 0,0.15325 -0.06215,0.29202 -0.1626,0.39248 -0.10045,0.10045 -0.23924,0.1626 -0.39247,0.1626 -0.07892,0 -0.15617,-0.01695 -0.22698,-0.04869 -0.07081,-0.03175 -0.1349,-0.0782 -0.18746,-0.13719 -0.01552,-0.01744 -0.01396,-0.04418 0.00348,-0.0597 0.01744,-0.01552 0.04418,-0.01396 0.0597,0.00348 0.04471,0.05018 0.09895,0.08957 0.15868,0.11635 0.05972,0.02677 0.12527,0.04107 0.19258,0.04107 0.12988,0 0.24749,-0.05266 0.33261,-0.13778 0.08511,-0.08512 0.13778,-0.20274 0.13778,-0.33262 0,-0.12988 -0.05267,-0.2475 -0.13778,-0.33262 -0.08512,-0.08512 -0.20273,-0.13778 -0.33261,-0.13778 -0.12781,0 -0.24392,0.05095 -0.32877,0.13379 -0.08488,0.08286 -0.13863,0.19766 -0.14167,0.32526z" id="_209787848"/><polygon class="fil2" id="_212238672" points="0.15781,0.77441 0.21699,0.70033 0.27614,0.62625 0.15782,0.62625 0.03949,0.62625 0.09866,0.70034 "/><path class="fil2" d="M0.7147 0.5942l0.22465 0c0.0233,0 0.04234,0.01904 0.04234,0.04233l0 0c0,0.02329 -0.01912,0.04233 -0.04234,0.04233l-0.26775 0c-0.02321,0 -0.04233,-0.01904 -0.04233,-0.04233l0 0c0,-0.00275 0.00026,-0.00544 0.00077,-0.00804l0 -0.03582 0 -0.22408 0 -0.04367c0,-0.0233 0.01904,-0.04234 0.04233,-0.04234l0 0c0.02329,0 0.04233,0.01912 0.04233,0.04234l0 0.04367 0 0.22408 0 0.00153z" id="_212243136"/></g></g></svg>'
    )
    editor.ui.registry.addIcon(
      'anonymize',
      '<svg id="Layer_1_1_" style="enable-background:new 0 0 64 64;" version="1.1" viewBox="0 0 64 64" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><path d="M25,15c0,0.55273,0.44775,1,1,1h12c0.55225,0,1-0.44727,1-1c0-2.80066-1.65747-5.21588-4.03973-6.33405  C35.59949,7.95679,36,7.02789,36,6c0-2.20605-1.79443-4-4-4s-4,1.79395-4,4c0,1.02789,0.40051,1.95679,1.03973,2.66595  C26.65747,9.78412,25,12.19934,25,15z M30,6c0-1.10254,0.89697-2,2-2s2,0.89746,2,2s-0.89697,2-2,2S30,7.10254,30,6z M32,10  c2.41455,0,4.43506,1.7207,4.89941,4h-9.79883C27.56494,11.7207,29.58545,10,32,10z"/><path d="M41,22c0,0.55273,0.44775,1,1,1h12c0.55225,0,1-0.44727,1-1c0-2.80066-1.65747-5.21588-4.03973-6.33405  C51.59949,14.95679,52,14.02789,52,13c0-2.20605-1.79443-4-4-4s-4,1.79395-4,4c0,1.02789,0.40051,1.95679,1.03973,2.66595  C42.65747,16.78412,41,19.19934,41,22z M46,13c0-1.10254,0.89697-2,2-2s2,0.89746,2,2s-0.89697,2-2,2S46,14.10254,46,13z M48,17  c2.41455,0,4.43506,1.7207,4.89941,4h-9.79883C43.56494,18.7207,45.58545,17,48,17z"/><path d="M48,41c-2.20557,0-4,1.79395-4,4c0,1.02789,0.40051,1.95679,1.03973,2.66595C42.65747,48.78412,41,51.19934,41,54  c0,0.55273,0.44775,1,1,1h12c0.55225,0,1-0.44727,1-1c0-2.80066-1.65747-5.21588-4.03973-6.33405  C51.59949,46.95679,52,46.02789,52,45C52,42.79395,50.20557,41,48,41z M46,45c0-1.10254,0.89697-2,2-2s2,0.89746,2,2s-0.89697,2-2,2  S46,46.10254,46,45z M52.89941,53h-9.79883c0.46436-2.2793,2.48486-4,4.89941-4S52.43506,50.7207,52.89941,53z"/><path d="M25,61c0,0.55273,0.44775,1,1,1h12c0.55225,0,1-0.44727,1-1c0-2.80066-1.65747-5.21588-4.03973-6.33405  C35.59949,53.95679,36,53.02789,36,52c0-2.20605-1.79443-4-4-4s-4,1.79395-4,4c0,1.02789,0.40051,1.95679,1.03973,2.66595  C26.65747,55.78412,25,58.19934,25,61z M30,52c0-1.10254,0.89697-2,2-2s2,0.89746,2,2s-0.89697,2-2,2S30,53.10254,30,52z M32,56  c2.41455,0,4.43506,1.7207,4.89941,4h-9.79883C27.56494,57.7207,29.58545,56,32,56z"/><path d="M57.96027,31.66595C58.59949,30.95679,59,30.02789,59,29c0-2.20605-1.79443-4-4-4s-4,1.79395-4,4  c0,1.02789,0.40051,1.95679,1.03973,2.66595C49.65747,32.78412,48,35.19934,48,38c0,0.55273,0.44775,1,1,1h12  c0.55225,0,1-0.44727,1-1C62,35.19934,60.34253,32.78412,57.96027,31.66595z M53,29c0-1.10254,0.89697-2,2-2s2,0.89746,2,2  s-0.89697,2-2,2S53,30.10254,53,29z M50.10059,37c0.46436-2.2793,2.48486-4,4.89941-4s4.43506,1.7207,4.89941,4H50.10059z"/><path d="M9,22c0,0.55273,0.44775,1,1,1h12c0.55225,0,1-0.44727,1-1c0-2.80066-1.65747-5.21588-4.03973-6.33405  C19.59949,14.95679,20,14.02789,20,13c0-2.20605-1.79443-4-4-4s-4,1.79395-4,4c0,1.02789,0.40051,1.95679,1.03973,2.66595  C10.65747,16.78412,9,19.19934,9,22z M14,13c0-1.10254,0.89697-2,2-2s2,0.89746,2,2s-0.89697,2-2,2S14,14.10254,14,13z M16,17  c2.41455,0,4.43506,1.7207,4.89941,4h-9.79883C11.56494,18.7207,13.58545,17,16,17z"/><path d="M9,54c0,0.55273,0.44775,1,1,1h12c0.55225,0,1-0.44727,1-1c0-2.80066-1.65747-5.21588-4.03973-6.33405  C19.59949,46.95679,20,46.02789,20,45c0-2.20605-1.79443-4-4-4s-4,1.79395-4,4c0,1.02789,0.40051,1.95679,1.03973,2.66595  C10.65747,48.78412,9,51.19934,9,54z M14,45c0-1.10254,0.89697-2,2-2s2,0.89746,2,2s-0.89697,2-2,2S14,46.10254,14,45z M16,49  c2.41455,0,4.43506,1.7207,4.89941,4h-9.79883C11.56494,50.7207,13.58545,49,16,49z"/><path d="M2,38c0,0.55273,0.44775,1,1,1h12c0.55225,0,1-0.44727,1-1c0-2.80066-1.65747-5.21588-4.03973-6.33405  C12.59949,30.95679,13,30.02789,13,29c0-2.20605-1.79443-4-4-4s-4,1.79395-4,4c0,1.02789,0.40051,1.95679,1.03973,2.66595  C3.65747,32.78412,2,35.19934,2,38z M7,29c0-1.10254,0.89697-2,2-2s2,0.89746,2,2s-0.89697,2-2,2S7,30.10254,7,29z M9,33  c2.41455,0,4.43506,1.7207,4.89941,4H4.10059C4.56494,34.7207,6.58545,33,9,33z"/><path d="M21,32c0,6.06543,4.93457,11,11,11s11-4.93457,11-11s-4.93457-11-11-11S21,25.93457,21,32z M32,23c4.9624,0,9,4.03711,9,9  s-4.0376,9-9,9s-9-4.03711-9-9S27.0376,23,32,23z"/></svg>'
    )
    editor.ui.registry.addIcon(
      'name_names',
      '<svg id="Layer_1" style="enable-background:new 0 0 80 80;" version="1.1" viewBox="0 0 80 80" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><path d="M19.3397217,15.1318359C20.3656616,14.0547485,21,12.6015015,21,11c0-3.3085938-2.6914062-6-6-6s-6,2.6914062-6,6  c0,1.6015015,0.6343384,3.0547485,1.6602783,4.1318359C7.4395142,15.7590942,5,18.598999,5,22v4c0,0.5527344,0.4477539,1,1,1h18  c0.5522461,0,1-0.4472656,1-1v-4C25,18.598999,22.5604858,15.7590942,19.3397217,15.1318359z M15,7c2.2055664,0,4,1.7939453,4,4  s-1.7944336,4-4,4s-4-1.7939453-4-4S12.7944336,7,15,7z M23,25H7v-3c0-2.7568359,2.2431641-5,5-5h6c2.7568359,0,5,2.2431641,5,5V25z  "/><path d="M19.3397217,39.1318359C20.3656616,38.0546875,21,36.6015625,21,35c0-3.3085938-2.6914062-6-6-6s-6,2.6914062-6,6  c0,1.6015625,0.6343384,3.0546875,1.6602783,4.1318359C7.4395142,39.7590332,5,42.598999,5,46v4c0,0.5527344,0.4477539,1,1,1h18  c0.5522461,0,1-0.4472656,1-1v-4C25,42.598999,22.5604858,39.7590332,19.3397217,39.1318359z M15,31c2.2055664,0,4,1.7939453,4,4  s-1.7944336,4-4,4s-4-1.7939453-4-4S12.7944336,31,15,31z M23,49H7v-3c0-2.7568359,2.2431641-5,5-5h6c2.7568359,0,5,2.2431641,5,5  V49z"/><path d="M19.3397217,63.1318359C20.3656616,62.0546875,21,60.6015625,21,59c0-3.3085938-2.6914062-6-6-6s-6,2.6914062-6,6  c0,1.6015625,0.6343384,3.0546875,1.6602783,4.1318359C7.4395142,63.7590332,5,66.598999,5,70v4c0,0.5527344,0.4477539,1,1,1h18  c0.5522461,0,1-0.4472656,1-1v-4C25,66.598999,22.5604858,63.7590332,19.3397217,63.1318359z M15,55c2.2055664,0,4,1.7939453,4,4  s-1.7944336,4-4,4s-4-1.7939453-4-4S12.7944336,55,15,55z M23,73H7v-3c0-2.7568359,2.2431641-5,5-5h6c2.7568359,0,5,2.2431641,5,5  V73z"/><path d="M74,12H33c-0.5522461,0-1,0.4472656-1,1s0.4477539,1,1,1h41c0.5522461,0,1-0.4472656,1-1S74.5522461,12,74,12z"/><path d="M74,18H33c-0.5522461,0-1,0.4472656-1,1s0.4477539,1,1,1h41c0.5522461,0,1-0.4472656,1-1S74.5522461,18,74,18z"/><path d="M74,42H33c-0.5522461,0-1,0.4472656-1,1s0.4477539,1,1,1h41c0.5522461,0,1-0.4472656,1-1S74.5522461,42,74,42z"/><path d="M74,36H33c-0.5522461,0-1,0.4472656-1,1s0.4477539,1,1,1h41c0.5522461,0,1-0.4472656,1-1S74.5522461,36,74,36z"/><path d="M74,66H33c-0.5522461,0-1,0.4472656-1,1s0.4477539,1,1,1h41c0.5522461,0,1-0.4472656,1-1S74.5522461,66,74,66z"/><path d="M74,60H33c-0.5522461,0-1,0.4472656-1,1s0.4477539,1,1,1h41c0.5522461,0,1-0.4472656,1-1S74.5522461,60,74,60z"/></svg>'
    )
    editor.ui.registry.addIcon(
      'fact_check',
      '<svg enable-background="new 0 0 64 64" id="Layer_1" version="1.1" viewBox="0 0 64 64" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><circle cx="32" cy="32" fill="none" r="24" stroke="#2C3E50" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="2"/><polygon fill="none" points="  37.1,25.4 41.6,42.1 24.9,37.6 " stroke="#2C3E50" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="2"/><polygon fill="none" points="  24.9,37.6 20.4,20.9 37.1,25.4 " stroke="#2C3E50" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="2"/><line fill="none" stroke="#2C3E50" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="2" x1="33.5" x2="24.3" y1="24.8" y2="34"/><line fill="none" stroke="#2C3E50" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="2" x1="29.9" x2="23.6" y1="24.1" y2="30.4"/><line fill="none" stroke="#2C3E50" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="2" x1="27.1" x2="22.2" y1="22.7" y2="27.6"/><line fill="none" stroke="#2C3E50" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-width="2" x1="23.6" x2="21.5" y1="22" y2="24.1"/></svg>'
    )
    const commentOptions = this.canUserComment ? 'addcomment showcomments' : 'showcomments'
    const options = this.canUserEdit ? `h1 h2 | bold italic | numlist bullist | ${commentOptions} | ai_assistant` : commentOptions
    editor.ui.registry.addContextToolbar('aitextselection', {
      predicate(node: any) {
        if (node.classList.contains('citation') || node.querySelector('span.citation') !== null) {
          return false
        }

        return !editor.selection.isCollapsed()
      },
      items: options,
      position: 'selection',
      scope: 'node',
    })

    editor.on('keydown', (e: KeyboardEvent) => {
      if (e.keyCode === 13) {
        setTimeout(() => {
          // Use setTimeout to wait for the new node to be created
          const newNode = editor.selection.getNode()
          if (newNode.textContent === '') {
            this.removeSentenceIds(newNode)
          }
        }, 0)
      }
    })

    editor.on('init', () => {
      if (this.call.minutesCitations !== null) {
        EditorCitations.initCitations({
          editor: this.editor,
          citations: this.call.minutesCitations,
          eventEmitters: {
            openDocument: this.openDocument,
            openMeeting: this.openMeeting,
          },
        })
      }
    })
  }

  getSentenceIds(node: HTMLElement) {
    const startSentenceId = node.getAttribute('data-start-sentence-id')
    const endSentenceId = node.getAttribute('data-end-sentence-id')
    return { startSentenceId, endSentenceId }
  }

  async createComment(req: any, done: any, fail: any) {
    const response = await lastValueFrom(
      this.http.post<{ conversationUid: string }>(`/api/calls/editor/${this.call.id}/comments`, {
        content: req.content,
      })
    )
    const conversationUid = response.conversationUid
    done({ conversationUid })
  }

  async lookupComments(req: any, done: any, fail: any) {
    const conversationUid = req.conversationUid
    const response = await lastValueFrom(
      this.http.get<{
        comments: any[]
      }>(`/api/calls/editor/${this.call.id}/comments/${conversationUid}`)
    )
    const comments = response.comments
    const data = {
      conversation: {
        uid: conversationUid,
        comments,
      },
    }
    done(data)
  }

  async replyToComment(req: any, done: any, fail: any) {
    const { conversationUid, content } = req
    const response = await lastValueFrom(
      this.http.post<{ commentUid: string }>(`/api/calls/editor/${this.call.id}/comments`, {
        content,
        conversationUid,
      })
    )
    const commentUid = response.commentUid
    done({ commentUid })
  }

  async editComment(req: any, done: any, fail: any) {
    const { commentUid, content } = req
    if (!commentUid) {
      fail('Failed to edit comment. Please try again shortly.')
      return
    }
    try {
      await lastValueFrom(
        this.http.put<void>(`/api/calls/editor/${this.call.id}/comments/${commentUid}`, {
          content,
        })
      )
      done({ canEdit: true })
    } catch (err: any) {
      if (err instanceof HttpErrorResponse) {
        if (err.status === 403) {
          done({ canEdit: false })
        } else {
          fail('Failed to edit comment. Please contact support.')
        }
      }
    }
  }

  async deleteComment(req: any, done: any, fail: any) {
    const conversationUid = req.conversationUid

    try {
      await lastValueFrom(this.http.delete(`/api/calls/editor/${this.call.id}/conversations/${conversationUid}`))
      done({ canDelete: true })
    } catch (err: any) {
      if (err instanceof HttpErrorResponse) {
        if (err.status === 403) {
          done({ canDelete: false })
        } else {
          fail('Failed to delete conversation. Please contact support.')
        }
      }
    }
  }

  async deleteAllComments(_req: any, done: any, fail: any) {
    try {
      await lastValueFrom(this.http.delete(`/api/calls/editor/${this.call.id}/comments`))
      done({ canDelete: true })
    } catch (err: any) {
      if (err instanceof HttpErrorResponse) {
        if (err.status === 403) {
          done({ canDelete: false })
        } else {
          fail('Failed to delete comments. Please contact support.')
        }
      }
    }
  }

  async deleteDeleteComment(req: any, done: any, fail: any) {
    try {
      await lastValueFrom(this.http.delete(`/api/calls/editor/${this.call.id}/comments/${req.commentUid}`))
      done({ canDelete: true })
    } catch (err: any) {
      if (err instanceof HttpErrorResponse) {
        if (err.status === 403) {
          done({ canDelete: false })
        } else {
          fail('Failed to delete comment. Please contact support.')
        }
      }
    }
  }

  expandSelectionToWordBoundaries(editor: any) {
    const expandRangeToWordBoundaries = (range: any) => {
      const isWordCharacter = (char: string) => char.match(/[\w'-.!?]/) // include alphanumeric characters as well as aphostrophe, hyphen, and punctuations that end a sentence
      const isSpace = (char: string) => char === ' '
      const lastCharacter = range.endContainer.textContent[range.endOffset - 1]
      const expandStart = () => {
        while (range.startOffset > 0 && isWordCharacter(range.startContainer.textContent[range.startOffset - 1])) {
          range.setStart(range.startContainer, range.startOffset - 1)
        }
      }
      const expandEnd = () => {
        const endTextLength = range.endContainer.textContent.length
        if (isSpace(lastCharacter)) {
          return
        }
        while (range.endOffset < endTextLength && isWordCharacter(range.endContainer.textContent[range.endOffset])) {
          range.setEnd(range.endContainer, range.endOffset + 1)
        }
      }
      expandStart()
      expandEnd()
    }
    const selection = editor.selection
    if (selection.isCollapsed()) {
      throw new Error('The selection is empty.')
    }
    const range = selection.getRng()
    expandRangeToWordBoundaries(range)
    selection.setRng(range)
  }

  async streamOutputUpdateDialogue(
    task: any,
    sentenceIdPairs: any,
    selectionContent: any,
    enableInsertButton: any,
    disableInsertButton: any,
    setContent: any,
    closeDialogue: any,
    timeoutInMilliseconds: any
  ) {
    const callId = this.call.id
    const eventSource = this.sendToLlmLiveAsynchronous(callId, task, sentenceIdPairs, selectionContent)
    const receivedTextPieces: string[] = []
    disableInsertButton()

    let isFirstMessageReceived = false
    const cancelTimeoutId = setTimeout(() => {
      if (!isFirstMessageReceived) {
        this.toastService.show('EDITOR.AI_ACTION_FAILED')
        closeDialogue()
        eventSource.close()
        if (cancelTimeoutId) {
          clearTimeout(cancelTimeoutId)
        }
      }
    }, timeoutInMilliseconds) // if no response is received within this time, close the connection

    const takingLongTimeoutId = setTimeout(() => {
      if (!isFirstMessageReceived) {
        this.toastService.show('EDITOR.AI_ACTION_SLOW')
        if (takingLongTimeoutId) {
          clearTimeout(takingLongTimeoutId)
        }
      }
    }, 15000)

    eventSource.onmessage = (event: any) => {
      this.logger.debug(`Received Live LLM message for task "${task}" at ${formatDateWithMilliseconds(new Date())}`)
      const data = JSON.parse(event.data)
      isFirstMessageReceived = true
      if (cancelTimeoutId) {
        clearTimeout(cancelTimeoutId)
      }
      if (takingLongTimeoutId) {
        clearTimeout(takingLongTimeoutId)
      }

      receivedTextPieces.push(data.result)
      const receivedText = receivedTextPieces.join('')
      setContent(receivedText)

      if (data.isFinal) {
        eventSource.close()
        enableInsertButton()
      }
    }
    return { eventSource, timeoutId: cancelTimeoutId }
  }

  private cleanSelectionContent(content: string): string {
    return content.replace(/<\/?[^>]+(>|$)/g, '') // TODO: Improve HTML tag removal
  }

  private async fetchSyncResponseWithTimeout(
    task: LlmAction,
    sentenceIdPairs: SentenceIdPair[],
    selectionContent: string,
    timeoutInSeconds: number
  ): Promise<LiveSyncResponse | null> {
    return await Promise.race<LiveSyncResponse | null>([
      this.sendToLlmLiveSynchronous(this.call.id, task, sentenceIdPairs, selectionContent),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutInSeconds * 1000)),
    ])
  }

  shouldDisplayInsertButton(response: any, task: LlmAction): boolean {
    if (task === 'fact_check') {
      const FactCheckResolution = response as FactCheckResolution[]
      return FactCheckResolution.some((response: FactCheckResolution) => response.answer === 'False' && response.newWording !== null)
    }
    return true
  }

  private async handleStreamingResponse(
    editor: any,
    task: LlmAction,
    taskDisplayName: string,
    selectionContent: string,
    sentenceIdPairs: SentenceIdPair[],
    timeoutInSeconds: number
  ) {
    await this.displayStreamingDialogue(editor, sentenceIdPairs, selectionContent, task, taskDisplayName, timeoutInSeconds)
  }

  private async handleNonStreamingResponse(
    editor: any,
    task: LlmAction,
    taskDisplayName: string,
    selectionContent: string,
    sentenceIdPairs: SentenceIdPair[],
    timeoutInSeconds: number
  ) {
    const takingTooLongTimeoutId = setTimeout(() => {
      this.toastService.show('EDITOR.AI_ACTION_SLOW')
    }, 15000)
    try {
      const syncResponse = await this.fetchSyncResponseWithTimeout(task, sentenceIdPairs, selectionContent, timeoutInSeconds)
      if (syncResponse === null) {
        this.toastService.show('EDITOR.AI_ACTION_FAILED')
        return
      }
      const content = this.formatDisplayContent(task, syncResponse)
      const showInsertButton = this.shouldDisplayInsertButton(syncResponse, task)
      this.displayNonStreamingDialogue(editor, taskDisplayName, content, showInsertButton, () =>
        this.insertText(editor, editor.selection, this.formatInsertionContent(task, syncResponse))
      )
    } finally {
      clearTimeout(takingTooLongTimeoutId)
    }
  }

  private async performLlmLiveRequest(editor: any, task: LlmAction, taskDisplayName: string, timeoutInSeconds: number, stream: boolean) {
    clearTimeout(this.loadingTextTimeout!)
    try {
      this.expandSelectionToWordBoundaries(editor)
    } catch (e) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      this.logger.error('Failed to expand selection to word boundaries', e)
      return
    }
    this.isLoading = true
    this.scrollTranscriptToSentences(editor)
    const selectionContent = this.cleanSelectionContent(editor.selection.getContent())
    const sentenceIdPairs = this.getSentenceIdsOfSelection(editor)
    try {
      if (stream) {
        await this.handleStreamingResponse(editor, task, taskDisplayName, selectionContent, sentenceIdPairs, timeoutInSeconds)
      } else {
        await this.handleNonStreamingResponse(editor, task, taskDisplayName, selectionContent, sentenceIdPairs, timeoutInSeconds)
      }
    } catch (e) {
      this.toastService.show('EDITOR.AI_ACTION_FAILED')
      console.log(e)
    } finally {
      this.isLoading = false
    }
  }

  formatFactCheckInsertionContent(responses: FactCheckResolution[]): string {
    if (responses.length === 1) {
      return responses[0].newWording!
    }
    return responses
      .map((response) => {
        if (response.answer === 'True' || response.answer === 'Unclear') {
          return response.statement
        }
        return response.newWording
      })
      .join(' ')
  }

  formatInsertionContent(task: LlmAction, content: any): string {
    if (task === 'fact_check') {
      return this.formatFactCheckInsertionContent(content as FactCheckResolution[])
    }
    return content
  }

  private formatDisplayContent(task: LlmAction, content: any): string {
    if (task === 'fact_check') {
      return this.formatFactCheckResolution(content as FactCheckResolution[])
    }
    return content
  }

  async insertText(editor: any, selection: any, textToInsert: string) {
    this.metricsService.sendMetric(this.call.id, MetricType.CLICK_LLM_LIVE_ACTION_INSERTION_OF_SUGGESTION)
    const range = selection.getRng()
    const startNode = range.startContainer
    const endNode = range.endContainer
    range.deleteContents()
    if (startNode.nodeType === Node.TEXT_NODE) {
      if (startNode === endNode) {
        range.insertNode(document.createTextNode(textToInsert))
      } else {
        startNode.textContent += textToInsert
      }
    } else if (startNode.nodeType === Node.ELEMENT_NODE) {
      const textNode = document.createTextNode(textToInsert)
      startNode.appendChild(textNode)
    }
    editor.selection.collapse()
    await this.saveContent(
      EditorCitations.getContentWithStrippedCitationsFromEditor({
        editor,
        citations: this.call.minutesCitations,
        eventEmitters: {
          openDocument: this.openDocument,
          openMeeting: this.openMeeting,
        },
      })
    )
  }

  displayNonStreamingDialogue(editor: any, title: string, content: string, showInsertButton: boolean, insert: () => void) {
    editor.windowManager.open({
      title,
      body: {
        type: 'panel',
        items: [
          {
            type: 'htmlpanel',
            html: content,
          },
        ],
      },
      buttons: [
        {
          type: 'cancel',
          text: this.translateService.instant('EDITOR.AI_ACTION_DIALOG.CLOSE'),
        },
        ...(showInsertButton
          ? [
              {
                type: 'submit',
                text: this.translateService.instant('EDITOR.AI_ACTION_DIALOG.INSERT'),
                primary: true,
              },
            ]
          : []),
      ],
      onSubmit: (api: any) => {
        api.close()
        insert()
      },
      initialData: {},
    })
  }

  getStreamingDialogueConfig(
    taskDisplayName: string,
    oldContent: string,
    shouldDisplayInsertButton: boolean,
    onSubmit: (api: any) => void,
    onClose: () => void
  ) {
    return {
      title: taskDisplayName,
      body: {
        type: 'panel',
        items: [
          {
            type: 'iframe',
            name: 'iframe',
            sandboxed: true,
            transparent: false,
            streamContent: true,
          },
        ],
      },
      buttons: [
        {
          type: 'cancel',
          name: 'closeButton',
          text: this.translateService.instant('EDITOR.AI_ACTION_DIALOG.DISCARD'),
        },
        ...(shouldDisplayInsertButton
          ? [{ type: 'submit', name: 'submitButton', text: this.translateService.instant('EDITOR.AI_ACTION_DIALOG.INSERT'), primary: true }]
          : []),
      ],
      initialData: {
        iframe: this.constructConfirmationDialogueHtml(oldContent, null),
      },
      size: 'medium',
      onSubmit,
      onClose,
    }
  }

  constructConfirmationDialogueHtml(selection: any, newContent: string | null) {
    const generatingText = this.translateService.instant('EDITOR.GENERATING')
    return `
        <style>
            .loader-container {
                display: flex;
            }
            .loader::after {
                content: '';
                display: inline-block;
                width: 1em;
                text-align: left;
                animation: ellipsis steps(4, end) 1s infinite;
            }
            @keyframes ellipsis {
                0%, 100% {
                    content: '';
                }
                25% {
                    content: '.';
                }
                50% {
                    content: '..';
                }
                75% {
                    content: '...';
                }
            }
        </style>
        <body onLoad="window.scrollTo(0,5000)">
        <div style="font-family: 'Calibri', sans-serif; margin-bottom: 10px;"><strong>${this.translateService.instant('EDITOR.AI_ACTION_DIALOG.CURRENT_SELECTION')}:</strong></div>
        <div style="color: grey; margin-bottom: 20px; font-family: 'Calibri', sans-serif; line-height: 20px;">${selection}</div>
        <div style="font-family: 'Calibri', sans-serif; margin-bottom: 10px;"><strong>${this.translateService.instant('EDITOR.AI_ACTION_DIALOG.NEW_CONTENT')}:</strong></div>
        <div style="color: green; font-family: 'Calibri', sans-serif; line-height: 20px;">
            ${newContent === null || newContent === '' ? `<div class="loader-container">${generatingText}<span class="loader"></span></div>` : newContent}
        </div>
      `
  }

  async displayStreamingDialogue(
    editor: any,
    sentenceIdPairs: SentenceIdPair[],
    oldContent: string,
    task: LlmAction,
    taskDisplayName: string,
    timeoutInSeconds: number
  ) {
    let sse: any = null
    let timeoutId: any = null
    let accumulatedText: string = ''
    const onSubmit = (api: any) => {
      api.close()
      this.insertText(editor, editor.selection, this.formatInsertionContent(task, accumulatedText))
    }
    const onClose = () => {
      if (sse) sse.close()
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
    let shouldDisplayInsertButton = this.shouldDisplayInsertButton(null, task)
    const dialogApi = await editor.windowManager.open(
      this.getStreamingDialogueConfig(taskDisplayName, oldContent, shouldDisplayInsertButton, onSubmit, onClose)
    )
    const response = await this.streamOutputUpdateDialogue(
      task,
      sentenceIdPairs,
      oldContent,
      () => dialogApi.setEnabled('submitButton', true),
      () => dialogApi.setEnabled('submitButton', false),
      (newContent: any) => {
        accumulatedText = newContent
        const shouldKeepInsertionButton = this.shouldDisplayInsertButton(newContent, task)
        if (shouldDisplayInsertButton !== shouldKeepInsertionButton) {
          shouldDisplayInsertButton = shouldKeepInsertionButton
          dialogApi.redial(this.getStreamingDialogueConfig(taskDisplayName, oldContent, shouldKeepInsertionButton, onSubmit, onClose))
        }
        dialogApi.setData({
          iframe: this.constructConfirmationDialogueHtml(oldContent, this.formatDisplayContent(task, newContent)),
        })
      },
      () => dialogApi.close(),
      timeoutInSeconds * 1000
    )
    sse = response.eventSource
    timeoutId = response.timeoutId
  }

  formatFactCheckResolution(responses: FactCheckResolution[]) {
    let contentHtml = '<div class="fact-check-container" style="display: block; padding: 10px;">'

    responses.forEach((response: FactCheckResolution) => {
      const answerColors: any = {
        True: 'green',
        False: 'red',
        Unclear: '#454545',
      }
      const answerText = {
        True: this.translateService.instant('EDITOR.AI_ACTION_DIALOG.TRUE'),
        False: this.translateService.instant('EDITOR.AI_ACTION_DIALOG.FALSE'),
        Unclear: this.translateService.instant('EDITOR.AI_ACTION_DIALOG.UNCLEAR'),
      }
      const answerColor = answerColors[response.answer] || 'black'
      const answer = answerText[response.answer]
      const newWording =
        response.newWording && response.answer === 'False'
          ? `<p><strong>${this.translateService.instant('EDITOR.AI_ACTION_DIALOG.SUGGESTED_WORDING')}:</strong> <span style="color: green">${response.newWording}</span></p>`
          : ''
      contentHtml += `
      <div class="fact-check-item" style="margin-bottom: 20px; border: 1px solid #ddd; padding: 10px; box-shadow: 0px 0px 5px rgba(0,0,0,0.1);">
        <p><strong>${this.translateService.instant('EDITOR.AI_ACTION_DIALOG.STATEMENT')}:</strong> ${response.statement}</p>
        <p><strong>${this.translateService.instant('EDITOR.AI_ACTION_DIALOG.ANSWER')}:</strong> <span style="color: ${answerColor}; font-weight: bold;">${answer}</span></p>
        <p><strong>${this.translateService.instant('EDITOR.AI_ACTION_DIALOG.REASON')}:</strong> ${response.reason}</p>${newWording}
      </div>`
    })
    contentHtml += '</div>'
    return contentHtml
  }

  getSentenceIdsOfSelection(editor: any): SentenceIdPair[] {
    const selectedBlocks = editor.selection.getSelectedBlocks()
    const uniquePairs = selectedBlocks
      .map((block: any) => {
        const { startSentenceId, endSentenceId } = this.getClosestSentenceIdsOfNode(block)
        return startSentenceId && endSentenceId ? { startSentenceId, endSentenceId } : null
      })
      .filter((pair: any) => pair !== null)
      .filter(
        (pair: any, index: any, self: any) =>
          index === self.findIndex((p: any) => p.startSentenceId === pair.startSentenceId && p.endSentenceId === pair.endSentenceId)
      )
    return uniquePairs
  }

  getClosestSentenceIdsOfNode(node: any) {
    const closestNode = node.closest('[data-start-sentence-id], [data-end-sentence-id]')
    if (!closestNode) {
      return { startSentenceId: null, endSentenceId: null }
    }
    const startSentenceId = closestNode.getAttribute('data-start-sentence-id')
    const endSentenceId = closestNode.getAttribute('data-end-sentence-id')
    return { startSentenceId, endSentenceId }
  }

  removeSentenceIds(node: HTMLElement) {
    node.removeAttribute('data-start-sentence-id')
    node.removeAttribute('data-end-sentence-id')
  }

  async saveContent(content: string, overrideSave$ = false) {
    if (this.save$.value === '' && !overrideSave$) {
      this.logger.info('No content to save. Skipping sending a request to the backend.')
      return
    }
    if (!this.hasLock) {
      this.logger.info('Not saving due to not having the lock.')
      return
    }
    try {
      await lastValueFrom(
        this.http.put(`/api/calls/${this.call.id}/editor-content`, {
          content,
        })
      )
      // Don't show the toast if the user is only a commenter
      if (this.canUserEdit) {
        this.toastService.show('EDITOR.CONTENT_SAVED')
      }
    } catch (err: any) {
      this.toastService.show('EDITOR.CONTENT_SAVE_FAILED')
      throw err
    }
  }

  private scrollTranscriptToSentences(editor: any) {
    const pairs = this.getSentenceIdsOfSelection(editor)
    if (pairs.length === 0) {
      return
    }
    const startSentenceId = pairs[0].startSentenceId
    const endSentenceId = pairs[pairs.length - 1].endSentenceId
    this.router.navigate([], {
      queryParams: { sentenceId: startSentenceId, endSentenceId: endSentenceId, highlight: true },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  private scrollTranscriptToSentenceIds(sentenceId: string, endSentenceId: string | null) {
    this.router.navigate([], {
      queryParams: { sentenceId, endSentenceId, highlight: true },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  private async sendToLlmLiveSynchronous(
    callId: number,
    action: LlmAction,
    sentenceIdPairs: SentenceIdPair[],
    selectionContent: string
  ): Promise<LiveSyncResponse> {
    return await lastValueFrom(
      this.http.post<LiveSyncResponse>(`/api/calls/${callId}/llm-live-sync`, {
        action,
        sentenceIdPairs,
        selectionContent,
      })
    )
  }

  private sendToLlmLiveAsynchronous(callId: number, action: LlmAction, sentenceIdPairs: any, selectionContent: any) {
    this.logger.debug(`Sending asynchronous Live LLM request for call ${callId} and action ${action} at ${formatDateWithMilliseconds(new Date())}`)
    const queryParams = new URLSearchParams({
      action,
      sentenceIdPairs: JSON.stringify(sentenceIdPairs),
      selectionContent,
    }).toString()
    const es = new EventSource(`/api/calls/${callId}/llm-live-async?${queryParams}`, {
      withCredentials: true,
    })

    es.onerror = (event: any) => {
      if (event.status === 401 || event.status === 403) {
        es.close()
      }
    }

    return es
  }

  private generateEditorInit(): any {
    const commentsFeatures = {
      tinycomments_mode: 'callback',
      tinycomments_create: this.createComment.bind(this),
      tinycomments_reply: this.replyToComment.bind(this),
      tinycomments_edit_comment: this.editComment.bind(this),
      tinycomments_delete: this.deleteComment.bind(this),
      tinycomments_delete_all: this.deleteAllComments.bind(this),
      tinycomments_delete_comment: this.deleteDeleteComment.bind(this),
      tinycomments_lookup: this.lookupComments.bind(this),
    }
    const commentsMenuOptions = {
      tc: {
        title: 'Comments',
        items: this.canUserComment ? 'addcomment showcomments deleteallconversations' : 'showcomments',
      },
    }
    const plugins = `lists link wordcount table tinycomments revisionhistory`
    return {
      plugins,
      menubar: false,
      removed_menuitems: 'subscript codeformat styles blocks fontfamily fontsize',
      toolbar: false,
      revisionhistory_allow_restore: true,
      revisionhistory_display_author: true,
      revisionhistory_fetch: () => this.fetchRevisions(),
      contextmenu: false,
      height: '100%',
      setup: (editor: any) => this.setupEditor(editor, this.translateService),
      branding: false,
      editable_root: true,
      menu: {
        ...commentsMenuOptions,
      },
      content_style: getEditorStyles(this.call.agendaDownloadUrl !== null || this.call.useTemplateForMinuting),
      ...commentsFeatures,
      statusbar: false,
      highlight_on_focus: false,
      browser_spellcheck: true,
    }
  }

  async fetchRevisions() {
    try {
      const revisions = await lastValueFrom(this.http.get<Revision[]>(`/api/calls/${this.call.id}/editor/revisions`))
      return revisions.map((revision) => ({
        ...revision,
        author: { ...revision.author, avatar: generateAvatar(revision.author?.name ?? null) },
      }))
    } catch (err: any) {
      this.toastService.show('EDITOR.REVISION_HISTORY_FETCH_FAILED')
      this.logger.error('Failed to fetch revisions', err)
      return []
    }
  }

  async reset() {
    this.isLoading = true
    try {
      const response = await lastValueFrom(this.http.put<{ editorContent: string }>(`/api/calls/${this.call.id}/reset-editor-content`, {}))
      this.editor.setContent(response.editorContent)

      this.isLoading = false
    } catch (err: any) {
      this.isLoading = false
      return
    }
  }

  obtainAndMaintainLockForEditor() {
    if (this.isDisabled) {
      return
    }
    this.editorLockSubscription = timer(0, 5000)
      .pipe(
        switchMap(() => this.http.post<LockEditorResponse>(`/api/calls/${this.call.id}/editor/lock`, {})),
        tap(async (response) => {
          const previouslyFailed = this.editorLockFailureResponse !== null && isEditorLockFailureResponse(this.editorLockFailureResponse)
          if (response.lockObtained && previouslyFailed) {
            await this.reloadCall()
          }
        })
      )
      .subscribe((response) => this.setEditorLockStatus(response))
  }

  ngOnDestroy() {
    // Save the final value
    this.saveContent(this.save$.value)
    this.saveSubscription.unsubscribe()
    if (this.editorLockSubscription !== null) {
      this.editorLockSubscription.unsubscribe()
    }
    if (this.queryParamsSubscription !== null) {
      this.queryParamsSubscription.unsubscribe()
    }
  }

  async reloadCall() {
    await this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(this.call.id) })
  }

  forceUnlock() {
    this.http
      .post<LockEditorResponse>(`/api/calls/${this.call.id}/editor/force-unlock`, {})
      .pipe(tap(() => this.reloadCall()))
      .subscribe((response) => this.setEditorLockStatus(response))
  }

  private setEditorLockStatus(status: LockEditorResponse) {
    if (status.lockObtained === this.hasLock) {
      this.isLocked.emit(!this.hasLock)
      return
    }
    if (isEditorLockFailureResponse(status)) {
      this.editorLockFailureResponse = status
      this.whenEditorIsInitialized().subscribe(() => {
        this.editor.mode.set('readonly')
      })
    } else {
      this.editorLockFailureResponse = null
      this.whenEditorIsInitialized().subscribe(() => {
        this.editor.mode.set('design')
      })
    }
    this.isLocked.emit(!this.hasLock)
  }

  showRevisionHistory() {
    this.editor.execCommand('revisionHistory')
    this.metricsService.sendMetric(this.call.id, MetricType.TOGGLE_REVISION_HISTORY)
  }

  private whenEditorIsInitialized() {
    return timer(0, 100).pipe(
      filter(() => this.editor?.initialized ?? false),
      take(1)
    )
  }

  async approveMissingAgenda() {
    await this.approveMissingAgendaMutation.mutateAsync(this.call.id)
  }

  uploadAgenda() {
    this.openAgendaUpload.emit()
  }

  scrollToTemplateItem(templateItemId: number) {
    if (this.editor === null || this.editor === undefined) {
      return
    }
    const element = this.editor.dom.select(`[data-template-item-id="${templateItemId}"]`)[0] ?? null
    if (element !== null) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
